import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { SessionUser } from "../types/auth";
import { buildAuthLoginHref, sanitizeReturnTo, type AuthLoginHrefOptions } from "../utils/auth/return-to";
import { setLoginStatusUserProperty } from "../utils/analytics/providers/ga4";
import { fetchSession } from "../query/user/userApiClient";

export interface AuthContextValue {
  user: SessionUser | null;
  /** Server-computed from the admin email allowlist; never derive this client-side. */
  hasBackofficeAccess: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (returnTo?: string, options?: AuthLoginHrefOptions) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export interface AuthProviderProps {
  initialUser: SessionUser | null;
  initialHasBackofficeAccess?: boolean;
  children: React.ReactNode;
}

/**
 * Single source of truth for session. Fetches /auth/session at most once per app load
 * when initialUser is null, then shares the result via context so all useAuth() consumers
 * get the same user without each triggering their own request.
 */
export function AuthProvider({ initialUser, initialHasBackofficeAccess, children }: AuthProviderProps) {
  const [user, setUser] = useState<SessionUser | null>(initialUser);
  const [hasBackofficeAccess, setHasBackofficeAccess] = useState<boolean>(
    initialHasBackofficeAccess ?? false
  );
  const [isLoading, setIsLoading] = useState<boolean>(
    !initialUser && typeof window !== "undefined"
  );
  const hasFetchedRef = useRef<boolean>(false);

  // Sync when SSR/hydration provides a new initialUser (e.g. after login redirect).
  // When initialUser is non-null, use it. When null, do not overwrite existing user so client-side
  // navigation to a page whose pageContext has no user (e.g. tools page) does not clear the session.
  useEffect(() => {
    if (initialUser) {
      setUser(initialUser);
      setHasBackofficeAccess(initialHasBackofficeAccess ?? false);
      hasFetchedRef.current = true;
      setIsLoading(false);
    }
  }, [initialUser?.id, initialUser, initialHasBackofficeAccess]);

  // Single session fetch when we have no user and haven't fetched yet
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (initialUser || hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    let cancelled = false;
    setIsLoading(true);

    // Shared per page load: this provider remounts on SRP <-> job page navigations (it sits in a
    // different LayoutDefault branch per page type), and useUserQuery asks for the same data.
    fetchSession()
      .then((data) => {
        if (cancelled) return;
        setUser(data.user);
        setHasBackofficeAccess(data.hasBackofficeAccess);
      })
      .catch(() => {
        if (!cancelled) {
          setUser(null);
          setHasBackofficeAccess(false);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [initialUser]);

  // Reflect resolved session state into GA4 for logged-in vs anonymous traffic
  // segmentation. No-ops until analytics consent is granted (see setLoginStatusUserProperty).
  useEffect(() => {
    if (isLoading) return;
    setLoginStatusUserProperty(Boolean(user));
  }, [user, isLoading]);

  const signIn = useCallback((returnTo?: string, options?: AuthLoginHrefOptions) => {
    const target =
      returnTo ??
      `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const sanitized = sanitizeReturnTo(target);
    window.location.href = buildAuthLoginHref(sanitized, options);
  }, []);

  const signOut = useCallback(() => {
    window.location.href = "/auth/logout";
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      hasBackofficeAccess,
      isLoading,
      isAuthenticated: Boolean(user),
      signIn,
      signOut,
    }),
    [user, hasBackofficeAccess, isLoading, signIn, signOut]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return ctx;
}

/** Returns auth context or undefined when outside AuthProvider. Use when hook order must stay stable (e.g. LayoutContent). */
export function useAuthOptional(): AuthContextValue | undefined {
  return useContext(AuthContext);
}

export { AuthContext };
